/**
 * useDashboard.js — Estado del dashboard dinámico de Oportunidades Comerciales.
 *
 * El hook expone CRUD + regeneración de widgets. La regeneración es síncrona
 * en el backend (Bedrock no streamea), así que el UI muestra spinner por widget
 * en lugar de polling.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getDashboardMeta,
  getDefaultDashboard,
  refreshWidget as apiRefreshWidget,
  createWidget as apiCreateWidget,
  updateWidget as apiUpdateWidget,
  deleteWidget as apiDeleteWidget,
  updateDashboard as apiUpdateDashboard,
  reorderWidgets as apiReorderWidgets,
  suggestWidget as apiSuggestWidget,
} from '../services/dashboardsService';

export const DashboardStatus = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
};

export function useDashboard(username) {
  const [dashboard, setDashboard] = useState(null);
  const [meta, setMeta] = useState(null);
  const [status, setStatus] = useState(DashboardStatus.IDLE);
  const [error, setError] = useState(null);

  // Set de widget IDs actualmente en estado 'generating' (regeneración optimista)
  const [generatingIds, setGeneratingIds] = useState(() => new Set());

  // Flag global: regenerar todo el dashboard
  const [refreshingAll, setRefreshingAll] = useState(false);

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // -----------------------------------------------------------------------
  // Helpers de estado
  // -----------------------------------------------------------------------

  const setGenerating = useCallback((widgetId, value) => {
    setGeneratingIds(prev => {
      const next = new Set(prev);
      if (value) next.add(widgetId); else next.delete(widgetId);
      return next;
    });
  }, []);

  const upsertWidget = useCallback((widget) => {
    if (!widget) return;
    setDashboard(prev => {
      if (!prev) return prev;
      const widgets = prev.widgets.map(w => w.id === widget.id ? widget : w);
      return { ...prev, widgets };
    });
  }, []);

  const removeWidgetLocal = useCallback((widgetId) => {
    setDashboard(prev => {
      if (!prev) return prev;
      return { ...prev, widgets: prev.widgets.filter(w => w.id !== widgetId) };
    });
  }, []);

  // -----------------------------------------------------------------------
  // Carga inicial
  // -----------------------------------------------------------------------

  const loadDashboard = useCallback(async () => {
    if (!username) return;
    setStatus(DashboardStatus.LOADING);
    setError(null);
    try {
      const [metaRes, dashRes] = await Promise.all([
        getDashboardMeta(username),
        getDefaultDashboard(username),
      ]);
      if (!isMountedRef.current) return;
      setMeta(metaRes);
      setDashboard(dashRes.dashboard);
      setStatus(DashboardStatus.READY);
    } catch (err) {
      console.error('[useDashboard] load failed:', err);
      if (isMountedRef.current) {
        setError(err.message || 'Error al cargar el dashboard');
        setStatus(DashboardStatus.ERROR);
      }
    }
  }, [username]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // -----------------------------------------------------------------------
  // Regeneración
  // -----------------------------------------------------------------------

  const refreshAll = useCallback(async ({ mode = 'fast' } = {}) => {
    if (!username || !dashboard) return;
    const targets = [...dashboard.widgets];
    if (targets.length === 0) return;

    setRefreshingAll(true);
    setError(null);
    // Feedback inmediato: marcar todos los widgets como "generando".
    setGeneratingIds(new Set(targets.map(w => w.id)));

    // IMPORTANTE: en vez de un solo POST /refresh que regenera los ~10 widgets
    // EN SERIE en el backend (una request de varios minutos, sin bytes de vuelta
    // → el gateway corta la conexión y el navegador tira "Failed to fetch"),
    // disparamos un refresh POR WIDGET con requests cortas e independientes.
    // Cada una dura segundos y los errores quedan aislados por widget. La
    // concurrencia se limita para no saturar los workers del backend ni el tope
    // diario de tokens.
    const CONCURRENCY = 3;
    const queue = [...targets];
    const failures = [];

    const worker = async () => {
      while (queue.length > 0) {
        const w = queue.shift();
        if (!w) break;
        try {
          const res = await apiRefreshWidget(dashboard.id, w.id, username, { mode });
          if (!isMountedRef.current) return;
          if (res?.widget) upsertWidget(res.widget);
          if (res && res.success === false) failures.push(w.title || `#${w.id}`);
        } catch (err) {
          console.error(`[useDashboard] refreshAll widget ${w.id} falló:`, err);
          failures.push(w.title || `#${w.id}`);
        } finally {
          if (isMountedRef.current) setGenerating(w.id, false);
        }
      }
    };

    try {
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, targets.length) }, worker)
      );
      if (isMountedRef.current && failures.length > 0) {
        setError(
          `No se pudieron actualizar ${failures.length} de ${targets.length} widgets. ` +
          'Probá regenerarlos individualmente con el botón ⟳ de cada tarjeta.'
        );
      }
    } finally {
      if (isMountedRef.current) {
        setRefreshingAll(false);
        setGeneratingIds(new Set());
      }
    }
  }, [username, dashboard, setGenerating, upsertWidget]);

  const refreshWidget = useCallback(async (widgetId, { mode = 'fast' } = {}) => {
    if (!username || !dashboard) return;
    setGenerating(widgetId, true);
    try {
      const res = await apiRefreshWidget(dashboard.id, widgetId, username, { mode });
      if (!isMountedRef.current) return;
      upsertWidget(res.widget);
    } catch (err) {
      console.error('[useDashboard] refreshWidget failed:', err);
      if (isMountedRef.current) setError(err.message || 'Error al regenerar widget');
    } finally {
      if (isMountedRef.current) setGenerating(widgetId, false);
    }
  }, [username, dashboard, setGenerating, upsertWidget]);

  // -----------------------------------------------------------------------
  // CRUD widgets
  // -----------------------------------------------------------------------

  /**
   * Pide a la IA una configuración completa de widget a partir de una
   * descripción libre. Devuelve { widget_type, chart_type, dataset, size,
   * title, prompt, reasoning } o null si falla.
   */
  const suggestWidget = useCallback(async (description) => {
    if (!username || !description?.trim()) return null;
    try {
      const res = await apiSuggestWidget(username, description.trim());
      return res?.suggestion || null;
    } catch (err) {
      console.error('[useDashboard] suggestWidget failed:', err);
      if (isMountedRef.current) setError(err.message || 'No se pudo generar la sugerencia');
      return null;
    }
  }, [username]);

  const addWidget = useCallback(async (payload, { generate = true } = {}) => {
    if (!username || !dashboard) return null;
    try {
      const res = await apiCreateWidget(dashboard.id, {
        username,
        ...payload,
        generate,
      });
      if (!isMountedRef.current) return null;
      setDashboard(prev => prev ? { ...prev, widgets: [...prev.widgets, res.widget] } : prev);
      return res.widget;
    } catch (err) {
      console.error('[useDashboard] addWidget failed:', err);
      if (isMountedRef.current) setError(err.message || 'Error al crear widget');
      return null;
    }
  }, [username, dashboard]);

  const editWidget = useCallback(async (widgetId, payload) => {
    if (!username || !dashboard) return null;
    try {
      const res = await apiUpdateWidget(dashboard.id, widgetId, { username, ...payload });
      if (!isMountedRef.current) return null;
      upsertWidget(res.widget);
      return res.widget;
    } catch (err) {
      console.error('[useDashboard] editWidget failed:', err);
      if (isMountedRef.current) setError(err.message || 'Error al editar widget');
      return null;
    }
  }, [username, dashboard, upsertWidget]);

  const removeWidget = useCallback(async (widgetId) => {
    if (!username || !dashboard) return false;
    // Optimistic: quitar primero
    const prevDash = dashboard;
    removeWidgetLocal(widgetId);
    try {
      await apiDeleteWidget(dashboard.id, widgetId, username);
      return true;
    } catch (err) {
      console.error('[useDashboard] removeWidget failed:', err);
      // Restaurar si falla
      if (isMountedRef.current) {
        setDashboard(prevDash);
        setError(err.message || 'Error al eliminar widget');
      }
      return false;
    }
  }, [username, dashboard, removeWidgetLocal]);

  // -----------------------------------------------------------------------
  // Dashboard meta
  // -----------------------------------------------------------------------

  const renameDashboard = useCallback(async (newName) => {
    if (!username || !dashboard) return;
    try {
      const res = await apiUpdateDashboard(dashboard.id, { username, name: newName });
      if (!isMountedRef.current) return;
      setDashboard(prev => prev ? { ...prev, ...res.dashboard, widgets: prev.widgets } : prev);
    } catch (err) {
      console.error('[useDashboard] renameDashboard failed:', err);
      if (isMountedRef.current) setError(err.message || 'Error al renombrar dashboard');
    }
  }, [username, dashboard]);

  const reorder = useCallback(async (orderedIds) => {
    if (!username || !dashboard) return;
    // Optimistic: reordenar localmente
    const prevDash = dashboard;
    const widgetsById = Object.fromEntries(dashboard.widgets.map(w => [w.id, w]));
    const newWidgets = orderedIds
      .map((id, idx) => widgetsById[id] ? { ...widgetsById[id], position: idx } : null)
      .filter(Boolean);
    setDashboard(prev => prev ? { ...prev, widgets: newWidgets } : prev);
    try {
      await apiReorderWidgets(dashboard.id, username, orderedIds);
    } catch (err) {
      console.error('[useDashboard] reorder failed:', err);
      if (isMountedRef.current) {
        setDashboard(prevDash);
        setError(err.message || 'Error al reordenar widgets');
      }
    }
  }, [username, dashboard]);

  // -----------------------------------------------------------------------
  // Computed
  // -----------------------------------------------------------------------

  const widgets = dashboard?.widgets || [];
  const isLoading = status === DashboardStatus.LOADING;
  const isReady = status === DashboardStatus.READY;
  const hasError = status === DashboardStatus.ERROR;

  return {
    // State
    dashboard,
    widgets,
    meta,
    status,
    error,
    isLoading,
    isReady,
    hasError,
    refreshingAll,
    isWidgetGenerating: (id) => generatingIds.has(id),

    // Actions
    loadDashboard,
    refreshAll,
    refreshWidget,
    addWidget,
    editWidget,
    removeWidget,
    renameDashboard,
    reorder,
    suggestWidget,

    // Reset error
    clearError: () => setError(null),
  };
}

export default useDashboard;
